/**
 * Phase 4 — Brief Requirements Panel.
 *
 * Surfaces TemplateContract gaps as actionable requirements with an
 * "Fill Automatically" CTA backed by an LLM auto-fill endpoint.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, RefreshCw, Sparkles, X } from 'lucide-react';
import {
  briefRequirementsApi,
  type AutoFillCandidate,
  type BriefRequirement,
  type BriefRequirementsReport,
  type RequirementStatus,
} from '@/services/api';

interface BriefRequirementsPanelProps {
  briefId: string;
  className?: string;
  /** Optional callback invoked after a requirement reaches resolution. */
  onResolved?: (requirementId: string, status: RequirementStatus) => void;
  /** Optional callback after a brief mutation (auto-fill applied). */
  onBriefUpdated?: (briefId: string) => void;
}

export function BriefRequirementsPanel({
  briefId,
  className = '',
  onResolved,
  onBriefUpdated,
}: BriefRequirementsPanelProps) {
  const [report, setReport] = useState<BriefRequirementsReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeAutoFill, setActiveAutoFill] = useState<{
    requirement: BriefRequirement;
    candidate: AutoFillCandidate;
  } | null>(null);
  const [autoFillLoadingFor, setAutoFillLoadingFor] = useState<string | null>(null);
  const [waiveLoadingFor, setWaiveLoadingFor] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await briefRequirementsApi.evaluate(briefId, {
        include_ineligible_details: true,
      });
      setReport(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to evaluate requirements');
    } finally {
      setLoading(false);
    }
  }, [briefId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleAutoFill = useCallback(
    async (req: BriefRequirement) => {
      setAutoFillLoadingFor(req.requirement_id);
      try {
        const candidate = await briefRequirementsApi.autoFill(briefId, req.requirement_id);
        setActiveAutoFill({ requirement: req, candidate });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Auto-fill failed');
      } finally {
        setAutoFillLoadingFor(null);
      }
    },
    [briefId],
  );

  const handleWaive = useCallback(
    async (req: BriefRequirement) => {
      setWaiveLoadingFor(req.requirement_id);
      try {
        const updated = await briefRequirementsApi.patch(briefId, req.requirement_id, {
          status: 'waived',
          resolved_by: 'user',
        });
        setReport(updated);
        onResolved?.(req.requirement_id, 'waived');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Waive failed');
      } finally {
        setWaiveLoadingFor(null);
      }
    },
    [briefId, onResolved],
  );

  const handleApplyAutoFill = useCallback(async () => {
    if (!activeAutoFill) return;
    const { requirement } = activeAutoFill;
    setAutoFillLoadingFor(requirement.requirement_id);
    try {
      // The proposed_changes map uses dotted paths. The server-side will
      // re-evaluate after the brief is updated. This implementation calls
      // PATCH on the requirement directly to mark it AUTO_FILLED so the
      // panel state reflects resolution. A separate brief mutation (via
      // the chat or strategy lens) is expected to apply the actual content.
      const updated = await briefRequirementsApi.patch(briefId, requirement.requirement_id, {
        status: 'auto_filled',
        resolved_by: 'auto_fill',
      });
      setReport(updated);
      onResolved?.(requirement.requirement_id, 'auto_filled');
      onBriefUpdated?.(briefId);
      setActiveAutoFill(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Apply auto-fill failed');
    } finally {
      setAutoFillLoadingFor(null);
    }
  }, [activeAutoFill, briefId, onResolved, onBriefUpdated]);

  const requirements = report?.requirements ?? [];
  const pending = requirements.filter((r) => r.status === 'pending');
  const resolved = requirements.filter((r) => r.status !== 'pending');

  return (
    <div className={`p-6 max-w-3xl mx-auto ${className}`}>
      <header className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Template Requirements</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Content gaps that block specific ad templates from being eligible for this brief.
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          aria-label="Refresh requirements"
          className="text-xs text-slate-600 hover:text-slate-900 transition flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-200 rounded-md disabled:opacity-50"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Refresh
        </button>
      </header>

      {report && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
            {report.eligible_count} templates available
          </span>
          {report.ineligible_count > 0 && (
            <span className="text-[11px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded">
              {report.ineligible_count} templates unavailable
            </span>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2 flex items-start gap-2 mb-3">
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!loading && requirements.length === 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-4 text-sm text-slate-500 flex items-center gap-2">
          <CheckCircle2 size={16} className="text-emerald-600" />
          No template requirements. All templates are eligible for this brief.
        </div>
      )}

      {pending.length > 0 && (
        <section className="mb-4">
          <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
            Pending ({pending.length})
          </h3>
          <ul className="space-y-2">
            {pending.map((req) => (
              <RequirementCard
                key={req.requirement_id}
                requirement={req}
                impactedTemplates={
                  report?.newly_eligible_if_filled?.[req.slot_id] ?? []
                }
                isAutoFilling={autoFillLoadingFor === req.requirement_id}
                isWaiving={waiveLoadingFor === req.requirement_id}
                onAutoFill={() => handleAutoFill(req)}
                onWaive={() => handleWaive(req)}
              />
            ))}
          </ul>
        </section>
      )}

      {resolved.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
            Resolved ({resolved.length})
          </h3>
          <ul className="space-y-2">
            {resolved.map((req) => (
              <li
                key={req.requirement_id}
                className="border border-slate-200 rounded-md p-3 bg-slate-50 flex items-center gap-2"
              >
                <CheckCircle2 size={14} className="text-emerald-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900">
                    {req.triggered_by_template_name || req.triggered_by_template}
                  </div>
                  <div className="text-xs text-slate-500">
                    Slot: {req.slot_id} — status: {req.status}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {activeAutoFill && (
        <AutoFillConfirmationPanel
          requirement={activeAutoFill.requirement}
          candidate={activeAutoFill.candidate}
          onApply={handleApplyAutoFill}
          onClose={() => setActiveAutoFill(null)}
          isLoading={autoFillLoadingFor === activeAutoFill.requirement.requirement_id}
        />
      )}
    </div>
  );
}

interface RequirementCardProps {
  requirement: BriefRequirement;
  impactedTemplates: string[];
  isAutoFilling: boolean;
  isWaiving: boolean;
  onAutoFill: () => void;
  onWaive: () => void;
}

function RequirementCard({
  requirement,
  impactedTemplates,
  isAutoFilling,
  isWaiving,
  onAutoFill,
  onWaive,
}: RequirementCardProps) {
  return (
    <li className="border border-amber-200 rounded-md p-3 bg-amber-50/50 hover:bg-amber-50 transition">
      <div className="flex items-start gap-2 mb-2">
        <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded uppercase tracking-wider">
          Pending
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">
            {requirement.triggered_by_template_name || requirement.triggered_by_template} —
            missing: {requirement.slot_id}
          </div>
          <p className="text-xs text-slate-600 mt-1">{requirement.suggested_action}</p>
          {impactedTemplates.length > 0 && (
            <p className="text-[11px] text-slate-500 mt-1.5">
              Unlocks: <span className="font-medium">{impactedTemplates.join(', ')}</span>
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onAutoFill}
          disabled={isAutoFilling || isWaiving}
          aria-label="Fill requirement automatically with AI"
          className="text-xs px-2.5 py-1.5 bg-brand-purple text-white rounded-md hover:bg-brand-purple/90 transition flex items-center gap-1.5 disabled:opacity-50"
        >
          {isAutoFilling ? (
            <Loader2 size={11} className="animate-spin" />
          ) : (
            <Sparkles size={11} />
          )}
          Fill Automatically
        </button>
        <button
          type="button"
          onClick={onWaive}
          disabled={isAutoFilling || isWaiving}
          aria-label="Waive this requirement"
          className="text-xs px-2.5 py-1.5 text-slate-700 border border-slate-300 rounded-md hover:bg-slate-100 transition disabled:opacity-50"
        >
          {isWaiving ? <Loader2 size={11} className="animate-spin" /> : 'Waive'}
        </button>
      </div>
    </li>
  );
}

interface AutoFillConfirmationPanelProps {
  requirement: BriefRequirement;
  candidate: AutoFillCandidate;
  isLoading: boolean;
  onApply: () => void;
  onClose: () => void;
}

function AutoFillConfirmationPanel({
  requirement,
  candidate,
  isLoading,
  onApply,
  onClose,
}: AutoFillConfirmationPanelProps) {
  const proposedEntries = Object.entries(candidate.proposed_changes ?? {});
  const confidencePct = Math.round((candidate.confidence ?? 0) * 100);

  return (
    <aside
      className="fixed top-20 right-6 w-96 max-h-[calc(100vh-6rem)] overflow-y-auto bg-white border border-slate-300 shadow-2xl rounded-lg z-50"
      role="complementary"
      aria-label="Auto-fill confirmation"
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <h3 className="text-sm font-semibold text-slate-900">
          Suggested {requirement.requirement_type.replace(/_/g, ' ')}
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close auto-fill panel"
          className="text-slate-400 hover:text-slate-700"
        >
          <X size={16} />
        </button>
      </header>

      <div className="p-4 space-y-3">
        <div>
          <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
            Proposed changes
          </div>
          {proposedEntries.length === 0 ? (
            <p className="text-xs text-slate-500 italic">No changes proposed.</p>
          ) : (
            <ul className="text-xs space-y-1.5">
              {proposedEntries.map(([path, value]) => (
                <li
                  key={path}
                  className="bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5"
                >
                  <span className="text-slate-500 font-mono text-[10px]">{path}</span>
                  <div className="text-slate-900 font-medium mt-0.5 break-words">
                    {String(value)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {candidate.reasoning && (
          <div>
            <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider mb-1">
              Reasoning
            </div>
            <p className="text-xs text-slate-700">{candidate.reasoning}</p>
          </div>
        )}

        <div className="text-[11px] text-slate-600">
          Confidence: <span className="font-semibold">{confidencePct}%</span>
        </div>

        {candidate.alternatives && candidate.alternatives.length > 0 && (
          <div>
            <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
              Alternatives
            </div>
            <ul className="text-xs space-y-1.5">
              {candidate.alternatives.map((alt, idx) => (
                <li
                  key={idx}
                  className="bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5"
                >
                  <div className="text-slate-700 mb-0.5">
                    {Object.entries(alt.proposed_changes ?? {}).map(([path, value]) => (
                      <div key={path} className="break-words">
                        <span className="text-slate-500 font-mono text-[10px]">{path}: </span>
                        <span className="text-slate-900">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                  {alt.reasoning && (
                    <div className="text-slate-500 italic mt-0.5">{alt.reasoning}</div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <footer className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50">
        <button
          type="button"
          onClick={onClose}
          className="text-xs px-3 py-1.5 text-slate-700 border border-slate-300 rounded-md hover:bg-white transition"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onApply}
          disabled={isLoading || proposedEntries.length === 0}
          className="text-xs px-3 py-1.5 bg-brand-purple text-white rounded-md hover:bg-brand-purple/90 transition flex items-center gap-1.5 disabled:opacity-50"
        >
          {isLoading ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
          Apply This
        </button>
      </footer>
    </aside>
  );
}
